<template>
  <el-drawer
    :model-value="visible"
    title="Seleccionar producto"
    size="60%"
    direction="btt"
    @close="handleClose"
  >
    <div style="padding: 0 15px">
      <el-alert
        v-if="etRecomendada"
        title="Sugerencia del Sistema"
        type="info"
        :description="`Ya ha seleccionado productos de la familia de ET ${etRecomendada}. Seleccione más productos de esta misma ET base para evitar la creación de una Especificación Técnica unificada.`"
        show-icon
        style="margin-bottom: 15px"
        :closable="false"
      />
    </div>

    <el-row
      :gutter="10"
      style="margin-bottom: 15px; padding: 0 15px"
    >
      <el-col :span="3">
        <el-input
          v-model="filtros.codigo"
          placeholder="Código"
          clearable
          @input="load"
        />
      </el-col>
      <el-col :span="4">
        <el-input
          v-model="filtros.codigoEquivalente"
          placeholder="Cód. Equivalente"
          clearable
          @input="load"
        />
      </el-col>
      <el-col :span="3">
        <el-input
          v-model="filtros.codigoAdicional"
          placeholder="Cód. Secundario"
          clearable
          @input="load"
        />
      </el-col>
      <el-col :span="3">
        <el-input
          v-model="filtros.et"
          placeholder="Nro ET"
          clearable
          @input="load"
        />
      </el-col>
      <el-col :span="3">
        <el-input
          v-model="filtros.etBase"
          placeholder="ET Base"
          clearable
          @input="load"
        />
      </el-col>
      <el-col :span="permitirAltaRapida ? 4 : 6">
        <el-input
          v-model="filtros.descripcion"
          placeholder="Descripción"
          clearable
          @input="load"
        />
      </el-col>
      <el-col :span="2">
        <el-button
          type="primary"
          style="width: 100%"
          @click="load"
          >Buscar</el-button
        >
      </el-col>
      <el-col
        v-if="permitirAltaRapida"
        :span="2"
      >
        <el-tooltip
          :disabled="puedeAltaRapida"
          content="Seleccione un cliente primero"
        >
          <span>
            <el-button
              type="success"
              plain
              style="width: 100%"
              :disabled="!puedeAltaRapida"
              @click="altaRapidaVisible = true"
            >
              <icon-add /> Alta rápida
            </el-button>
          </span>
        </el-tooltip>
      </el-col>
    </el-row>

    <el-table
      v-loading="loading"
      :data="productosBuscados"
      style="width: 100%"
      :highlight-current-row="false"
      empty-text="Sin datos"
      border
      height="500px"
      :row-class-name="tableRowClassName"
    >
      <el-table-column
        prop="CodigoProducto"
        label="Código Prod."
        width="130"
        sortable
      />
      <el-table-column
        prop="CodigoEquivalente"
        label="Cód. Equivalente"
        width="140"
        sortable
      />
      <el-table-column
        prop="CodigoAdicional"
        label="Cód. Secundario"
        width="140"
        sortable
      />
      <el-table-column
        prop="Descripcion"
        label="Descripción"
        min-width="200"
        sortable
      />
      <el-table-column
        prop="CodigoBase"
        label="ET Base"
        width="110"
        sortable
      >
        <template #default="scope">
          <span style="font-weight: bold; color: #409eff">
            {{ scope.row.CodigoBase || scope.row.CodigoET }}
          </span>
        </template>
      </el-table-column>
      <el-table-column
        prop="CodigoET"
        label="Nro ET"
        width="160"
        sortable
      >
        <template #default="scope">
          <div style="display: flex; align-items: center">
            <span
              style="
                font-weight: bold;
                width: 45px;
                text-align: right;
                margin-right: 12px;
                color: #606266;
              "
            >
              {{ scope.row.CodigoET || '-' }}
            </span>

            <div>
              <el-tag
                v-if="esSeleccionado(scope.row)"
                type="info"
                size="small"
                effect="dark"
              >
                Ya agregado
              </el-tag>

              <el-tag
                v-else-if="etRecomendada && scope.row.CodigoBase === etRecomendada"
                type="success"
                size="small"
                effect="dark"
              >
                Sugerido
              </el-tag>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        prop="VersionET"
        label="Versión"
        width="90"
        align="center"
      >
        <template #default="scope">
          <el-tag
            v-if="scope.row.VersionET"
            type="warning"
            size="small"
            effect="Light"
          >
            V{{ scope.row.VersionET }}
          </el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="ClienteNombre"
        label="Cliente(s)"
        min-width="200"
      />

      <el-table-column
        label="Acciones"
        width="130"
        align="center"
      >
        <template #default="scope">
          <el-button
            :type="etRecomendada && scope.row.CodigoET !== etRecomendada ? 'warning' : 'primary'"
            plain
            size="small"
            @click.prevent="emit('select-poducto', scope.row)"
          >
            Seleccionar
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <modal-alta-rapida-producto
      v-model="altaRapidaVisible"
      :clientes="clientes"
      :codigo-unidad-medida-default="codigoUnidadMedidaDefault"
      @created="onProductoCreado"
    />
  </el-drawer>
</template>

<script setup>
import {
  watch,
  ref,
  reactive,
  computed,
} from 'vue';
import { ElNotification } from 'element-plus';
import IconAdd from '~icons/mdi/add';
import ModalAltaRapidaProducto from '@/shared/components/modal_alta_rapida_producto.vue';
import { useMaterialStore } from '@/stores/materiales_store.js';
import { useAuthStore } from '@/views/auth/auth_store';
import { canEditModule } from '@/shared/permissions/permission.utils.js';

const materialStore = useMaterialStore();

const props = defineProps({
  visible: { type: Boolean, default: false },
  codigoElementoGrupo: { type: Number },
  clientes: { type: Array, default: () => [] },
  etRecomendada: { type: Number, default: null },
  codigosYaSeleccionados: { type: Array, default: () => [] },
  permitirAltaRapida: { type: Boolean, default: false },
  codigoUnidadMedidaDefault: { type: Number, default: null },
});

const emit = defineEmits(['select-poducto', 'close-drawer']);

const authStore = useAuthStore();
const altaRapidaVisible = ref(false);
const puedeAltaRapida = computed(
  () => props.clientes.length > 0 && canEditModule(authStore.usuario(), 'materiales'),
);

const loading = ref(false);
const productosBuscados = ref([]);
const codigoNuevo = ref(null);
const filtros = reactive({
  codigo: '',
  descripcion: '',
  codigoEquivalente: '',
  codigoAdicional: '',
  et: '',
  etBase: '',
});

const handleClose = () => {
  emit('close-drawer');
};

const esSeleccionado = (row) => {
  const key = `${row.CodigoProducto || row.Codigo}-${row.CodigoET || 'null'}`;
  return props.codigosYaSeleccionados.includes(key);
};

const tableRowClassName = ({ row }) => {
  if (codigoNuevo.value && String(row.CodigoProducto) === codigoNuevo.value) {
    return 'row-nuevo';
  }
  if (esSeleccionado(row)) {
    return 'row-seleccionado';
  }
  if (props.etRecomendada) {
    return row.CodigoBase === props.etRecomendada ? 'row-recomendada' : 'row-advertencia';
  }
  return '';
};

const load = async () => {
  if (!props.visible) return;
  loading.value = true;
  try {
    const clientesIds = props.clientes.join(',');

    productosBuscados.value = await materialStore.buscarProductosConETHTTP({
      clientes: clientesIds,
      codigo: filtros.codigo,
      descripcion: filtros.descripcion,
      codigoEquivalente: filtros.codigoEquivalente,
      codigoAdicional: filtros.codigoAdicional,
      et: filtros.et,
      etBase: filtros.etBase,
    });
  } finally {
    loading.value = false;
  }
};

const resetFiltros = () => {
  codigoNuevo.value = null;
  filtros.codigo = '';
  filtros.descripcion = '';
  filtros.codigoEquivalente = '';
  filtros.codigoAdicional = '';
  filtros.et = '';
  filtros.etBase = props.etRecomendada ? String(props.etRecomendada) : '';
};

// Tras el alta rápida se recarga la lista completa (con los filtros por defecto) y se
// asegura que el producto nuevo aparezca, aunque el filtro de ET base lo excluya.
const onProductoCreado = async ({ Codigo }) => {
  resetFiltros();
  codigoNuevo.value = String(Codigo);
  await load();

  const yaEnLista = productosBuscados.value.some(
    (p) => String(p.CodigoProducto) === codigoNuevo.value,
  );
  if (!yaEnLista) {
    const encontrados = await materialStore.buscarProductosConETHTTP({
      clientes: props.clientes.join(','),
      codigo: codigoNuevo.value,
      descripcion: '',
      codigoEquivalente: '',
      codigoAdicional: '',
      et: '',
      etBase: '',
    });
    const nuevo = (encontrados || []).find((p) => String(p.CodigoProducto) === codigoNuevo.value);
    if (nuevo) productosBuscados.value = [nuevo, ...productosBuscados.value];
  }

  ElNotification({
    title: 'Producto creado',
    message: 'Selecciónelo de la lista para agregarlo al detalle.',
    type: 'success',
  });
};

watch(
  () => props.visible,
  (v) => {
    if (v) {
      resetFiltros();
      load();
    }
  },
  { immediate: true },
);
</script>

<style>
.el-table .row-recomendada {
  background-color: #f0f9eb !important;
}

.el-table .row-advertencia {
  background-color: #fdf6ec !important;
}

.el-table .row-nuevo {
  background-color: #ecf5ff !important;
}

.el-table .row-recomendada:hover > td {
  background-color: #e1f3d8 !important;
}

.el-table .row-advertencia:hover > td {
  background-color: #faecd8 !important;
}
</style>
